import { useMutation, useQuery, useQueryClient } from 'react-query';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

// Mirrors the backend MetaPurchaseTriggerType. Defined locally to keep the
// meta-capi feature self-contained and decoupled from the shared library build.
export type MetaPurchaseTriggerType = 'step' | 'custom_field';

export interface MetaCapiConfig {
  enabled: boolean;
  accessTokenSet: boolean;
  datasetId: string | null;
  whatsappBusinessAccountId: string | null;
  graphApiVersion: string;
  testEventCode: string | null;
  defaultCurrency: string;
  purchaseTriggerType: MetaPurchaseTriggerType | null;
  purchaseTriggerStepId: string | null;
  purchaseTriggerFieldName: string | null;
  purchaseTriggerFieldValue: string | null;
}

export interface MetaCapiConfigFormValues {
  enabled: boolean;
  accessToken: string;
  datasetId: string;
  whatsappBusinessAccountId: string;
  graphApiVersion: string;
  testEventCode: string;
  defaultCurrency: string;
  purchaseTriggerType: MetaPurchaseTriggerType | '';
  purchaseTriggerStepId: string;
  purchaseTriggerFieldName: string;
  purchaseTriggerFieldValue: string;
}

export const useMetaCapiConfig = (agencyId: number) =>
  useQuery({
    queryKey: [QueryKeys.getMetaCapiConfig, agencyId],
    queryFn: async (): Promise<MetaCapiConfig> => {
      const response = await client.get<MetaCapiConfig>(
        `agencies/${agencyId}/meta-capi/config`,
      );
      return response.data;
    },
    enabled: Boolean(agencyId),
    refetchOnWindowFocus: true,
  });

export const useUpdateMetaCapiConfig = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: MetaCapiConfigFormValues) => {
      // omitWhenEmpty: '' → omit the key entirely (undefined).
      //   Used for format-constrained fields where empty means "don't change".
      // clearWhenEmpty: '' → null.
      //   Used for fields the user can intentionally clear from the UI.
      //   The backend treats null as an explicit write of '' (clearing the value).
      const omitWhenEmpty = (v: string) => (v.trim() !== '' ? v : undefined);
      const clearWhenEmpty = (v: string) => (v.trim() !== '' ? v : null);

      const payload: Record<string, unknown> = {
        enabled: values.enabled,
        // format-constrained: '' means "don't change"
        datasetId: omitWhenEmpty(values.datasetId),
        graphApiVersion: omitWhenEmpty(values.graphApiVersion),
        defaultCurrency: omitWhenEmpty(values.defaultCurrency),
        // clearable: '' means "erase this value"
        whatsappBusinessAccountId: clearWhenEmpty(values.whatsappBusinessAccountId),
        testEventCode: clearWhenEmpty(values.testEventCode),
        // '' → null = "no trigger" (Lead only)
        purchaseTriggerType: values.purchaseTriggerType || null,
        purchaseTriggerStepId: clearWhenEmpty(values.purchaseTriggerStepId),
        purchaseTriggerFieldName: clearWhenEmpty(values.purchaseTriggerFieldName),
        purchaseTriggerFieldValue: clearWhenEmpty(values.purchaseTriggerFieldValue),
      };

      // accessToken: '' = preserve existing stored token (never clear it).
      if (values.accessToken && values.accessToken.trim() !== '') {
        payload.accessToken = values.accessToken;
      }

      await client.put(`agencies/${agencyId}/meta-capi/config`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getMetaCapiConfig]);
    },
  });
};
